import { FC, useState } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
} from 'components/common';
import { IApplicationDataFormatted, IApplicationsAnalystTableProps, tableColumnsNames } from './types';
import { FormattedMessage } from 'react-intl';
import { SortDirectionEnum } from '../../../legacy/shared-types';
import { useAnalystApplications } from 'queries/applications/useAnalystApplications';
import { FormattedTableCell } from 'pages/ApplicationsListingPage/ApplicationsTable/FormattedTableCell';
import { ApplicationForListingDto } from 'pages/ApplicationsListingPage/ApplicationsTable/types';

export const ApplicationsAnalystTable: FC<IApplicationsAnalystTableProps> = ({ searchInputValue }) => {
  const [order, setOrder] = useState<SortDirectionEnum>(SortDirectionEnum.DESC);
  const [orderBy, setOrderBy] = useState<keyof ApplicationForListingDto>('submittedAt');
  const { data: allApplications = [], isLoading } = useAnalystApplications();

  const formattedApplications = allApplications?.map(
    (application: ApplicationForListingDto): IApplicationDataFormatted => ({
      id: application.referenceNumber || application.id,
      applicant: application.applicant,
      coApplicant: application.coApplicant,
      status: application.status,
      type: application.type,
      createdAt: application.createdAt || application.submittedAt,
      submittedAt: application.submittedAt,
      brokerEmail: application.email,
    }),
  );

  const sortedRows = formattedApplications?.sort((a: any, b: any) => {
    if (a[orderBy] < b[orderBy]) {
      return order === SortDirectionEnum.ASC ? -1 : 1;
    }

    if (a[orderBy] > b[orderBy]) {
      return order === SortDirectionEnum.ASC ? 1 : -1;
    }
    return 0;
  });

  const filteredBySearchInputValue = sortedRows?.filter(row => {
    const rowValues = Object.values(row);
    return rowValues.some((value: any) => value.toString()?.toLowerCase().includes(searchInputValue?.toLowerCase()));
  });

  const sortHandler = (column: keyof ApplicationForListingDto) => {
    const isAsc = order === SortDirectionEnum.ASC;
    const isSameColumn = orderBy === column;

    setOrder(isAsc && isSameColumn ? SortDirectionEnum.DESC : SortDirectionEnum.ASC);
    setOrderBy(column);
  };

  return (
    <Paper sx={{ width: '100%', mb: 2 }}>
      <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
        <Table aria-label='collapsible table'>
          <TableHead>
            <TableRow>
              <TableCell />
              {tableColumnsNames.map(columnName => (
                <TableCell key={columnName}>
                  <TableSortLabel
                    data-value={columnName}
                    active={orderBy === columnName}
                    direction={order}
                    onClick={() => sortHandler(columnName as keyof ApplicationForListingDto)}
                  >
                    <FormattedMessage id={`application.${columnName}`} />
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody isLoading={isLoading} columnsNumber={tableColumnsNames.length}>
            {filteredBySearchInputValue?.length ? (
              filteredBySearchInputValue.map((row: IApplicationDataFormatted, index: number) => (
                <TableRow key={index}>
                  <TableCell />
                  {Object.entries(row).map(([objectKey, value]: [string, any]) => (
                    <TableCell key={objectKey}>
                      <FormattedTableCell objectKey={objectKey} value={value} />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell sx={{ textAlign: 'center' }} colSpan={9}>
                  <FormattedMessage id={'common.itemsNotFound'} />
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};
